import { Box, Button, Grid, InputAdornment, TextField, Typography } from '@mui/material';
import React from 'react';
import { Controller, Path, useFormContext } from 'react-hook-form';

interface EmployeeDetailsForm {
  openingBalances: {
    leaveBalances: {
      annualLeaveBalance: number;
      sickLeaveBalance: number;
    };
    asAtDate: string;
    ordinaryEarnings: {
      ordinaryTimeEarnings: number;
      superGuarantee: number;
      tax: number;
      bonusesCommission: number;
      directorsFee: number;
      employerContributionsRESC: number;
      superSalarySacrifice: number;
      reimbursement: number;
      overtime: number;
    };
    paidLeave: {
      annualLeave: number;
      sickLeave: number;
      longServiceLeave: number;
      paidParentalLeave: number;
      ancillaryAndDefenceLeave: number;
      workersCompensation: number;
    };
    allowance: {
      awardTransportPayments: number;
      centsPerKM: number;
      laundry: number;
      overtimeMeal: number;
      qualifications: number;
      task: number;
      tool: number;
      travel: number;
      jobKeeperTopUp: number;
      otherGeneral: number;
      otherHomeOffice: number;
      otherNonDeductible: number;
      otherTransportFares: number;
      otherUniform: number;
      otherPrivateVehicle: number;
    };
    deductions: {
      preTaxFees: number;
      preTaxWorkplaceGiving: number;
      postTaxFees: number;
      postTaxWorkplaceGiving: number;
      childSupportGarnishees: number;
      childSupportDeductions: number;
    };
    fringeBenefits: {
      fringeBenefitsTaxable: number;
    };
  };
}

type FormFieldPaths = Path<EmployeeDetailsForm>;

const OpeningBalancesTab: React.FC = () => {
  const {
    control,
    formState: { errors },
    resetField
  } = useFormContext<EmployeeDetailsForm>();

  const handleClearSection = (section: keyof typeof fieldsMap) => {
    fieldsMap[section]?.forEach((field) => resetField(field as FormFieldPaths));
  };

  const fieldsMap = {
    leaveBalances: [
      'openingBalances.leaveBalances.annualLeaveBalance',
      'openingBalances.leaveBalances.sickLeaveBalance'
    ] as FormFieldPaths[],
    ordinaryEarnings: [
      'openingBalances.ordinaryEarnings.ordinaryTimeEarnings',
      'openingBalances.ordinaryEarnings.superGuarantee',
      'openingBalances.ordinaryEarnings.tax',
      'openingBalances.ordinaryEarnings.bonusesCommission',
      'openingBalances.ordinaryEarnings.directorsFee',
      'openingBalances.ordinaryEarnings.employerContributionsRESC',
      'openingBalances.ordinaryEarnings.superSalarySacrifice',
      'openingBalances.ordinaryEarnings.reimbursement',
      'openingBalances.ordinaryEarnings.overtime'
    ] as FormFieldPaths[],
    paidLeave: [
      'openingBalances.paidLeave.annualLeave',
      'openingBalances.paidLeave.sickLeave',
      'openingBalances.paidLeave.longServiceLeave',
      'openingBalances.paidLeave.paidParentalLeave',
      'openingBalances.paidLeave.ancillaryAndDefenceLeave',
      'openingBalances.paidLeave.workersCompensation'
    ] as FormFieldPaths[],
    allowance: [
      'openingBalances.allowance.awardTransportPayments',
      'openingBalances.allowance.centsPerKM',
      'openingBalances.allowance.laundry',
      'openingBalances.allowance.overtimeMeal',
      'openingBalances.allowance.qualifications',
      'openingBalances.allowance.task',
      'openingBalances.allowance.tool',
      'openingBalances.allowance.travel',
      'openingBalances.allowance.jobKeeperTopUp',
      'openingBalances.allowance.otherGeneral',
      'openingBalances.allowance.otherHomeOffice',
      'openingBalances.allowance.otherNonDeductible',
      'openingBalances.allowance.otherTransportFares',
      'openingBalances.allowance.otherUniform',
      'openingBalances.allowance.otherPrivateVehicle'
    ] as FormFieldPaths[],
    deductions: [
      'openingBalances.deductions.preTaxFees',
      'openingBalances.deductions.preTaxWorkplaceGiving',
      'openingBalances.deductions.postTaxFees',
      'openingBalances.deductions.postTaxWorkplaceGiving',
      'openingBalances.deductions.childSupportGarnishees',
      'openingBalances.deductions.childSupportDeductions'
    ] as FormFieldPaths[],
    fringeBenefits: ['openingBalances.fringeBenefits.fringeBenefitsTaxable'] as FormFieldPaths[],
    asAtDate: ['openingBalances.asAtDate'] as FormFieldPaths[]
  };

  const getEndAdornment = (fieldName: string) => {
    // Time-related fields
    if (fieldName.includes('LeaveBalance')) {
      return 'hr';
    }
    // Monetary fields
    if (
      fieldName.includes('Earnings') ||
      fieldName.includes('Tax') ||
      fieldName.includes('Commission') ||
      fieldName.includes('Fee') ||
      fieldName.includes('Contributions') ||
      fieldName.includes('Sacrifice') ||
      fieldName.includes('Reimbursement') ||
      fieldName.includes('Allowance') ||
      fieldName.includes('Deductions') ||
      fieldName.includes('Benefits')
    ) {
      return '$';
    }
    // Percentage fields
    if (fieldName.includes('Rate') || fieldName.includes('Percent')) {
      return '%';
    }
    // Distance-related fields
    if (fieldName.includes('KM') || fieldName.includes('Distance')) {
      return 'km';
    }
    return undefined;
  };

  const renderSection = (
    title: string,
    sectionKey: keyof typeof fieldsMap,
    fields: Array<{
      name: FormFieldPaths;
      label: string;
      type?: string;
      customAdornment?: string;
    }>
  ) => (
    <Box sx={{ mb: 3 }}>
      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <Typography
          variant="subtitle1"
          fontWeight="bold"
          sx={{ fontSize: { xs: '1rem', sm: '1.1rem' } }}
        >
          {title}
        </Typography>
        <Button
          variant="outlined"
          onClick={() => handleClearSection(sectionKey)}
          size="small"
          sx={{
            textTransform: 'none',
            fontSize: { xs: '0.8125rem', sm: '0.875rem' }
          }}
        >
          Clear Section
        </Button>
      </Box>

      <Grid container spacing={1}>
        {fields.map((field) => {
          const adornment = field.customAdornment || getEndAdornment(field.name);
          return (
            <Grid item xs={12} sm={6} key={field.name}>
              <Controller
                name={field.name}
                control={control}
                render={({ field: controllerField }) => (
                  <TextField
                    {...controllerField}
                    label={field.label}
                    type={field.type || 'number'}
                    defaultValue={0}
                    InputProps={{
                      endAdornment: adornment ? (
                        <InputAdornment position="end">{adornment}</InputAdornment>
                      ) : null
                    }}
                    error={
                      !!(errors as any)?.openingBalances?.[field.name.split('.')[1]]?.[
                        field.name.split('.')[2]
                      ]
                    }
                    helperText={
                      (errors as any)?.openingBalances?.[field.name.split('.')[1]]?.[
                        field.name.split('.')[2]
                      ]?.message || ''
                    }
                    fullWidth
                    margin="dense"
                    size="small"
                  />
                )}
              />
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );

  return (
    <Box sx={{ p: { xs: 1, sm: 2 } }}>
      {/* Leave Balances Section */}
      {renderSection('Leave Balances', 'leaveBalances', [
        {
          name: 'openingBalances.leaveBalances.annualLeaveBalance',
          label: 'Annual leave balance',
          customAdornment: 'hr'
        },
        {
          name: 'openingBalances.leaveBalances.sickLeaveBalance',
          label: 'Sick leave balance',
          customAdornment: 'hr'
        }
      ])}

      {/* As At Date Section */}
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="subtitle1"
          fontWeight="bold"
          sx={{ mb: 2, fontSize: { xs: '1rem', sm: '1.1rem' } }}
        >
          As At Date
        </Typography>
        <Grid container spacing={1}>
          <Grid item xs={12} sm={6}>
            <Controller
              name="openingBalances.asAtDate"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="As at date"
                  type="date"
                  
                  error={!!errors.openingBalances?.asAtDate}
                  helperText={errors.openingBalances?.asAtDate?.message || ''}
                  fullWidth
                  margin="dense"
                  size="small"
                  InputLabelProps={{ shrink: true }}
                />
              )}
            />
          </Grid>
        </Grid>
      </Box>

      {/* Ordinary Earnings Section */}
      {renderSection('Ordinary Earnings', 'ordinaryEarnings', [
        {
          name: 'openingBalances.ordinaryEarnings.ordinaryTimeEarnings',
          label: 'Ordinary time earnings',
          customAdornment: '$'
        },
        {
          name: 'openingBalances.ordinaryEarnings.superGuarantee',
          label: 'Super guarantee',
          customAdornment: '$'
        },
        { name: 'openingBalances.ordinaryEarnings.tax', label: 'Tax', customAdornment: '$' },
        {
          name: 'openingBalances.ordinaryEarnings.bonusesCommission',
          label: 'Bonuses/Commission',
          customAdornment: '$'
        },
        {
          name: 'openingBalances.ordinaryEarnings.directorsFee',
          label: 'Directors fee',
          customAdornment: '$'
        },
        {
          name: 'openingBalances.ordinaryEarnings.employerContributionsRESC',
          label: 'Employer contributions (RESC)',
          customAdornment: '$'
        },
        {
          name: 'openingBalances.ordinaryEarnings.superSalarySacrifice',
          label: 'Super salary sacrifice',
          customAdornment: '$'
        },
        {
          name: 'openingBalances.ordinaryEarnings.reimbursement',
          label: 'Reimbursement',
          customAdornment: '$'
        },
        {
          name: 'openingBalances.ordinaryEarnings.overtime',
          label: 'Overtime',
          customAdornment: '$'
        }
      ])}

      {/* Paid Leave Section */}
      {renderSection('Paid Leave', 'paidLeave', [
        {
          name: 'openingBalances.paidLeave.annualLeave',
          label: 'Annual leave',
          customAdornment: '$'
        },
        { name: 'openingBalances.paidLeave.sickLeave', label: 'Sick leave', customAdornment: '$' },
        {
          name: 'openingBalances.paidLeave.longServiceLeave',
          label: 'Long service leave',
          customAdornment: '$'
        },
        {
          name: 'openingBalances.paidLeave.paidParentalLeave',
          label: 'Paid parental leave',
          customAdornment: '$'
        },
        {
          name: 'openingBalances.paidLeave.ancillaryAndDefenceLeave',
          label: 'Ancillary and defence leave',
          customAdornment: '$'
        },
        {
          name: 'openingBalances.paidLeave.workersCompensation',
          label: 'Workers compensation',
          customAdornment: '$'
        }
      ])}

      {/* Allowance Section */}
      {renderSection('Allowance', 'allowance', [
        {
          name: 'openingBalances.allowance.awardTransportPayments',
          label: 'Award transport payments',
          customAdornment: '$'
        },
        {
          name: 'openingBalances.allowance.centsPerKM',
          label: 'Cents per KM',
          customAdornment: '¢/km'
        },
        { name: 'openingBalances.allowance.laundry', label: 'Laundry', customAdornment: '$' },
        {
          name: 'openingBalances.allowance.overtimeMeal',
          label: 'Overtime meal',
          customAdornment: '$'
        },
        {
          name: 'openingBalances.allowance.qualifications',
          label: 'Qualifications',
          customAdornment: '$'
        },
        { name: 'openingBalances.allowance.task', label: 'Task', customAdornment: '$' },
        { name: 'openingBalances.allowance.tool', label: 'Tool', customAdornment: '$' },
        { name: 'openingBalances.allowance.travel', label: 'Travel', customAdornment: '$' },
        {
          name: 'openingBalances.allowance.jobKeeperTopUp',
          label: 'Job keeper top up',
          customAdornment: '$'
        },
        {
          name: 'openingBalances.allowance.otherGeneral',
          label: 'Other general',
          customAdornment: '$'
        },
        {
          name: 'openingBalances.allowance.otherHomeOffice',
          label: 'Other home office',
          customAdornment: '$'
        },
        {
          name: 'openingBalances.allowance.otherNonDeductible',
          label: 'Other non deductible',
          customAdornment: '$'
        },
        {
          name: 'openingBalances.allowance.otherTransportFares',
          label: 'Other transport fares',
          customAdornment: '$'
        },
        {
          name: 'openingBalances.allowance.otherUniform',
          label: 'Other uniform',
          customAdornment: '$'
        },
        {
          name: 'openingBalances.allowance.otherPrivateVehicle',
          label: 'Other private vehicle',
          customAdornment: '$'
        }
      ])}

      {/* Deductions Section */}
      {renderSection('Deductions', 'deductions', [
        {
          name: 'openingBalances.deductions.preTaxFees',
          label: 'Pre tax fees',
          customAdornment: '$'
        },
        {
          name: 'openingBalances.deductions.preTaxWorkplaceGiving',
          label: 'Pre tax workplace giving',
          customAdornment: '$'
        },
        {
          name: 'openingBalances.deductions.postTaxFees',
          label: 'Post tax fees',
          customAdornment: '$'
        },
        {
          name: 'openingBalances.deductions.postTaxWorkplaceGiving',
          label: 'Post tax workplace giving',
          customAdornment: '$'
        },
        {
          name: 'openingBalances.deductions.childSupportGarnishees',
          label: 'Child support garnishees',
          customAdornment: '$'
        },
        {
          name: 'openingBalances.deductions.childSupportDeductions',
          label: 'Child support deductions',
          customAdornment: '$'
        }
      ])}

      {/* Fringe Benefits Section */}
      {renderSection('Fringe Benefits', 'fringeBenefits', [
        {
          name: 'openingBalances.fringeBenefits.fringeBenefitsTaxable',
          label: 'Fringe benefits taxable',
          customAdornment: '$'
        }
      ])}
    </Box>
  );
};

export default OpeningBalancesTab;
